export type ClientType = "Registered" | "Unregistered" | "Inward" | string;

const lastDigits = (value: string): number | null => {
  const match = String(value || "").match(/(\d+)(?!.*\d)/);
  if (!match?.[1]) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) ? n : null;
};

const pad4 = (n: number) => String(n).padStart(4, "0");
const pad5 = (n: number) => String(n).padStart(5, "0");

function inferTypeFromStoredId(s: string): ClientType | "" {
  const v = String(s || "").trim();
  if (/^REG-/i.test(v)) return "Registered";
  if (/^NREG-/i.test(v)) return "Unregistered";
  if (/^BHT-/i.test(v)) return "Inward";
  return "";
}

/** Duty roster aggregates expose `regType`; legacy rows use registration tier instead of category. */
const REGISTRATION_TIER_AS_REGISTERED = new Set([
  "Regular Client",
  "One Year",
  "Life Time",
]);

function resolveStoredClientCategory(client: any): ClientType {
  const raw = String(client.clientType || client.regType || "").trim();
  if (REGISTRATION_TIER_AS_REGISTERED.has(raw)) return "Registered";
  return raw as ClientType;
}

/**
 * Legacy Registered rows often keep the full authoritative id only in `refNo`
 * while `registrationNo` holds a shortened or superseded number. Prefer
 * `REG-` + full stored ref whenever the two diverge.
 */
function formatLegacyRegisteredFromRef(refNo: string): string {
  const body = String(refNo || "")
    .trim()
    .replace(/^\s*REG-?\s*/i, "")
    .trim();
  return body ? `REG-${body}` : "";
}

function isLegacyRegisteredDisplay(
  registrationNo: string,
  refNo: string,
): boolean {
  const regT = String(registrationNo).trim();
  const refT = String(refNo).trim();
  return refT !== "" && refT !== regT;
}

/**
 * Formats client identifiers system-wide:
 * - Registered: REG-0000
 * - Unregistered: NREG-0000
 * - Inward: BHT-00000 (5-digit sequence)
 */
export function formatClientIdentifier(client: any): string {
  if (!client) return "";

  let type: ClientType = resolveStoredClientCategory(client);
  const registrationNo = client.registrationNo || "";
  const refNo = client.refNo || "";
  const nonRegNo = client.nonRegNo || "";
  const bhtNumber = client.bhtNumber || "";

  if (!type && refNo) type = inferTypeFromStoredId(refNo);
  if (!type && registrationNo) type = inferTypeFromStoredId(registrationNo);
  if (!type && nonRegNo) type = inferTypeFromStoredId(nonRegNo);
  if (!type && bhtNumber) type = inferTypeFromStoredId(bhtNumber);

  if (type === "Registered") {
    if (isLegacyRegisteredDisplay(registrationNo, refNo)) {
      return formatLegacyRegisteredFromRef(refNo);
    }
    const source =
      registrationNo.trim() !== ""
        ? registrationNo
        : refNo.trim() !== ""
          ? refNo
          : "";
    if (!source) return "";
    const n = lastDigits(source);
    return n !== null ? `REG-${pad4(n)}` : source;
  }

  if (type === "Unregistered") {
    const source =
      nonRegNo.trim() !== ""
        ? nonRegNo
        : refNo.trim() !== ""
          ? refNo
          : "";
    if (!source) return "";
    const n = lastDigits(source);
    return n !== null ? `NREG-${pad4(n)}` : source;
  }

  if (type === "Inward") {
    const source =
      bhtNumber.trim() !== ""
        ? bhtNumber
        : refNo.trim() !== ""
          ? refNo
          : "";
    if (!source) return "";
    const n = lastDigits(source);
    return n !== null ? `BHT-${pad5(n)}` : source;
  }

  return refNo || registrationNo || nonRegNo || bhtNumber || "";
}

/** Digits slug for monthly invoice composition (reports style). */
export function clientRefSlugForComposition(refNo: string): string {
  const n = lastDigits(String(refNo || ""));
  if (n !== null) {
    const s = String(refNo || "");
    if (/^BHT-/i.test(s)) return pad5(n);
    return pad4(n);
  }
  return String(refNo || "").replace(/\D/g, "");
}

export type ClientIdentifierValidation =
  | { valid: true }
  | { valid: false; message: string };

/**
 * Strict input rules before normalize:
 * - Digits only (1–4 for REG/NREG, 1–5 for BHT), or
 * - Full canonical prefix + fixed-width digits (REG-0000, NREG-0000, BHT-00000).
 * Rejects legacy/mixed formats (e.g. REG0001, REG-1, wrong prefix).
 */
export function validateClientIdentifierInput(
  clientType: ClientType,
  raw: string,
): ClientIdentifierValidation {
  const trimmed = String(raw || "").trim();
  if (!trimmed) {
    return { valid: false, message: "Client identifier is required." };
  }

  if (clientType === "Registered") {
    if (/^(NREG|BHT)-/i.test(trimmed)) {
      return {
        valid: false,
        message: "Registered clients must use REG- (e.g. REG-0001).",
      };
    }
    if (/^\d{1,4}$/.test(trimmed)) return { valid: true };
    if (/^REG-\d{4}$/i.test(trimmed)) return { valid: true };
    return {
      valid: false,
      message:
        "Enter 1–4 digits (e.g. 0001) or REG-0001 with exactly 4 digits after REG-.",
    };
  }

  if (clientType === "Unregistered") {
    if (/^(REG-|BHT-)/i.test(trimmed)) {
      return {
        valid: false,
        message: "Non-registered clients must use NREG- (e.g. NREG-0001).",
      };
    }
    if (/^\d{1,4}$/.test(trimmed)) return { valid: true };
    if (/^NREG-\d{4}$/i.test(trimmed)) return { valid: true };
    return {
      valid: false,
      message:
        "Enter 1–4 digits (e.g. 0001) or NREG-0001 with exactly 4 digits after NREG-.",
    };
  }

  if (clientType === "Inward") {
    if (/^(REG-|NREG-)/i.test(trimmed)) {
      return {
        valid: false,
        message: "Inward clients must use BHT- (e.g. BHT-00001).",
      };
    }
    if (/^\d{1,5}$/.test(trimmed)) return { valid: true };
    if (/^BHT-\d{5}$/i.test(trimmed)) return { valid: true };
    return {
      valid: false,
      message:
        "Enter 1–5 digits (e.g. 00001) or BHT-00001 with exactly 5 digits after BHT-.",
    };
  }

  return { valid: false, message: `Unknown client type: ${clientType}` };
}

/**
 * Canonical storage form for type-specific client identifiers.
 * Mirrors server `normalizeClientIdentifier`.
 */
export function normalizeClientIdentifier(
  clientType: ClientType,
  raw: string,
): string {
  const check = validateClientIdentifierInput(clientType, raw);
  if (check.valid === false) {
    throw new Error(check.message);
  }

  const trimmed = String(raw || "").trim();

  if (clientType === "Registered") {
    if (/^\d{1,4}$/.test(trimmed)) {
      return `REG-${pad4(Number(trimmed))}`;
    }
    const m = trimmed.match(/^REG-(\d{4})$/i)!;
    return `REG-${m[1]}`;
  }
  if (clientType === "Unregistered") {
    if (/^\d{1,4}$/.test(trimmed)) {
      return `NREG-${pad4(Number(trimmed))}`;
    }
    const m = trimmed.match(/^NREG-(\d{4})$/i)!;
    return `NREG-${m[1]}`;
  }
  if (clientType === "Inward") {
    if (/^\d{1,5}$/.test(trimmed)) {
      return `BHT-${pad5(Number(trimmed))}`;
    }
    const m = trimmed.match(/^BHT-(\d{5})$/i)!;
    return `BHT-${m[1]}`;
  }

  throw new Error(`Unknown clientType: ${clientType}`);
}

function monthlyInvoicePrefixForClient(client: any): string {
  let type: ClientType = resolveStoredClientCategory(client);
  const refNo = client?.refNo || "";
  const registrationNo = client?.registrationNo || "";
  const nonRegNo = client?.nonRegNo || "";
  const bhtNumber = client?.bhtNumber || "";

  if (!type && refNo) type = inferTypeFromStoredId(refNo);
  if (!type && registrationNo) type = inferTypeFromStoredId(registrationNo);
  if (!type && nonRegNo) type = inferTypeFromStoredId(nonRegNo);
  if (!type && bhtNumber) type = inferTypeFromStoredId(bhtNumber);

  if (type === "Unregistered") return "NRG";
  if (type === "Inward") return "INW";
  return "REG";
}

/** Monthly billing invoice id: prefix + digit slug + YYYY + MM (e.g. REG0001202601). */
export function composeMonthlyInvoiceNumber(
  client: any,
  year: number,
  month: number,
): string {
  const prefix = monthlyInvoicePrefixForClient(client);
  const canonical = formatClientIdentifier(client);
  const slug = clientRefSlugForComposition(
    canonical || client?.refNo || "",
  );
  const monthStr = String(month).padStart(2, "0");
  return `${prefix}${slug}${year}${monthStr}`;
}
